import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';

import { parseSpaEntityData } from '@common/components/SpaEntity/SpaEntityContainer';
import { Routes } from '@common/constants';
import { getSpaTreatmentTimeslots } from '@features/spa/hooks/useGetSpaTreatmentTimeslots';
import { useReturnToTimeWhenSlotTaken } from '@features/spa/hooks/useReturnToTimeWhenSlotTaken';
import { slotSeats } from '@features/spa/hooks/useTimeslotAvailability';
import { findSlotForArrivalTime } from '@features/spa/utils/resolveSelectedSlotPricing';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { SpaEntityType } from 'shared-types/Spa';

import { SpaTreatmentFormValues } from '../SpaTreatmentBookingV2.types';

type UseSubmitTreatmentParams = {
  form: UseFormReturn<SpaTreatmentFormValues>;
  spaEntity: SpaEntityType | undefined;
  propertyId: number;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  hasExtras: boolean;
  onInvalid?: () => void;
};

export function useSubmitTreatment({
  form,
  spaEntity,
  propertyId,
  language,
  id,
  onClose,
  onInvalid,
}: UseSubmitTreatmentParams) {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const isStillOffered = useCallback(
    async (date: string, arrivalTime: NonNullable<SpaTreatmentFormValues['arrivalTime']>) => {
      const slots = await getSpaTreatmentTimeslots({
        spaTreatmentId: id,
        startDate: date,
        language,
        propertyId,
        numberOfDays: 1,
        numberOfGuests: 1,
      });
      const slot = findSlotForArrivalTime(slots, arrivalTime);
      return !!slot && slotSeats(slot, form.getValues('guests')?.length);
    },
    [id, language, propertyId, form]
  );

  const returnToTimeIfSlotTaken = useReturnToTimeWhenSlotTaken({
    form: form as never,
    id,
    bookingRoute: Routes.SPA_TREATMENT_BOOKING,
    isStillOffered,
  });

  const addToBasket = useCallback(
    (data: SpaTreatmentFormValues) => {
      if (!spaEntity) return;
      const parsedEntity = parseSpaEntityData(data as any, spaEntity, 'spa-treatments');
      parsedEntity.guestDetails = parsedEntity.guestDetails.map((guest: any, i: number) => {
        const formGuest = data.guests[i];
        if (formGuest?.loggedInUser) {
          const { id: _id, ...rest } = guest;
          return { ...rest, loggedInUser: formGuest.loggedInUser };
        }
        return guest;
      });
      const basketId = Cookie.get('basketId');

      const promise = basketId
        ? dispatch(
            updateBasket(
              propertyId,
              basketId,
              language,
              {
                items: [...currentBasketItems, parsedEntity],
              },
              false,
              true
            ) as any
          )
        : dispatch(
            createBasket(
              propertyId,
              language,
              {
                items: [parsedEntity],
              },
              true
            ) as any
          );

      Promise.resolve(promise)
        .then(() => onClose(true))
        .catch(() => returnToTimeIfSlotTaken())
        .finally(() =>
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.includes('spaTreatment') && query.queryKey.includes('timeslots'),
          })
        );
    },
    [
      spaEntity,
      currentBasketItems,
      dispatch,
      propertyId,
      language,
      queryClient,
      onClose,
      returnToTimeIfSlotTaken,
    ]
  );

  const handleSubmit = form.handleSubmit(addToBasket as any, onInvalid);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.SPA_TREATMENT_BOOKING,
      query: { id, bookingStep: 'extras' },
    });
  }, onInvalid);

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
  }, [router, id]);

  return {
    handleSubmit,
    handleNextStep,
    handleBack,
  };
}
